import { Controller } from "@hotwired/stimulus"

// Controller to request and save user's location for distance calculations
// Used on dashboard to enable nearby gyms sorting
export default class extends Controller {
  static targets = ["button"]

  connect() {
    // Only auto-request location if this is not a button (i.e., hidden div)
    // Button will handle manual requests via updateLocation action
    if (!this.hasButtonTarget) {
      // Only request location if user hasn't been asked recently (avoid spam)
      // Check if we've requested in the last 5 minutes
      const lastRequest = sessionStorage.getItem('last_location_request')
      const now = Date.now()
      
      // Request location if:
      // 1. Never requested before, OR
      // 2. Last request was more than 5 minutes ago
      if (!lastRequest || (now - parseInt(lastRequest)) > 5 * 60 * 1000) {
        this.requestLocation()
        sessionStorage.setItem('last_location_request', now.toString())
      }
    }
  }

  updateLocation(event) {
    // Manual location update triggered by button click
    event.preventDefault()
    this.getCurrentPosition(true) // Pass true to show feedback
  }

  async requestLocation() {
    // Check if geolocation is available
    if (!navigator.geolocation) {
      return
    }

    // Check permissions first (if Permissions API is available)
    if (navigator.permissions && navigator.permissions.query) {
      try {
        const permissionStatus = await navigator.permissions.query({ name: 'geolocation' })
        
        // If permission is denied, don't request
        if (permissionStatus.state === 'denied') {
          return
        }
        
        // If granted or prompt, proceed
        if (permissionStatus.state === 'granted' || permissionStatus.state === 'prompt') {
          this.getCurrentPosition()
        }
      } catch (error) {
        // Permissions API not supported, try direct request
        this.getCurrentPosition()
      }
    } else {
      // Permissions API not available, try direct request
      this.getCurrentPosition()
    }
  }

  getCurrentPosition(showFeedback = false) {
    // Show loading state if button exists
    if (showFeedback && this.hasButtonTarget) {
      this.buttonTarget.disabled = true
      this.buttonTarget.classList.add('loading')
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const latitude = position.coords.latitude
        const longitude = position.coords.longitude
        
        // Save position to backend
        this.saveUserPosition(latitude, longitude, showFeedback)
      },
      (error) => {
        // Reset button state
        if (showFeedback && this.hasButtonTarget) {
          this.buttonTarget.disabled = false
          this.buttonTarget.classList.remove('loading')
        }

        if (showFeedback) {
          // Show error feedback to user
          alert('Unable to get your location. Please check your browser permissions.')
        } else {
          // Silently fail - location is optional for dashboard functionality
          console.log('Location not available:', error.message)
        }
      },
      {
        enableHighAccuracy: true, // Use high accuracy for manual requests
        timeout: 10000,
        maximumAge: 0 // Don't use cached location for manual requests
      }
    )
  }

  saveUserPosition(latitude, longitude, showFeedback = false) {
    // Save user position to backend for distance calculations
    fetch('/map/update_position', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      },
      body: JSON.stringify({
        latitude: latitude,
        longitude: longitude
      })
    })
      .then(response => {
        // Reset button state
        if (showFeedback && this.hasButtonTarget) {
          this.buttonTarget.disabled = false
          this.buttonTarget.classList.remove('loading')
        }

        if (response.ok) {
          // Reload page to update nearby gyms with new distances
          if (showFeedback) {
            window.location.reload()
          }
        } else {
          throw new Error('Failed to save position')
        }
      })
      .catch(error => {
        // Reset button state
        if (showFeedback && this.hasButtonTarget) {
          this.buttonTarget.disabled = false
          this.buttonTarget.classList.remove('loading')
        }

        console.warn('Failed to save user position:', error)
        if (showFeedback) {
          alert('Failed to save your location. Please try again.')
        }
      })
  }
}
